import { apiGet, apiPost, apiPatch, apiDelete } from './client';

// Phase 113 (MKS-208) — backdated-hours requests: the only way hours reach a
// locked project-month. Shaped like `joinRequests.ts`, which this mirrors.

export interface BackdatedHoursRequest {
  id: number;
  userId: number;
  projectId: number;
  projectName: string | null;
  date: string;
  hours: number;
  task: string | null;
  description: string | null;
  reason: string;
  state: 'pending' | 'approved' | 'declined';
  declineReason: string | null;
  approvedBy: { id: number; name: string | null } | null;
  entryId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface PendingBackdatedHoursRequest extends BackdatedHoursRequest {
  userName: string | null;
  userEmail: string;
  /** `2026-08` — the project-month the hours would land in. */
  month: string;
  monthLocked: boolean;
  /**
   * D-09 — the month already carries invoiced hours. Approving sends these
   * hours to the to-be-invoiced queue rather than onto the issued invoice, and
   * the approver has to be told so before they commit.
   */
  monthInvoiced: boolean;
}

/**
 * MKS-266 — an approved request as the correction surface sees it: what the
 * employee asked for (the base fields) alongside what the entry it created
 * looks like RIGHT NOW. The two differ when the approver adjusted the values
 * in the approve dialog, or when somebody corrected them afterwards.
 */
export interface ApprovedBackdatedHoursRequest extends BackdatedHoursRequest {
  userName: string | null;
  userEmail: string;
  /** `2026-08` — the project-month the hours landed in. */
  month: string;
  entry: {
    id: number;
    date: string;
    hours: number;
    task: string | null;
    description: string | null;
    /** On an issued invoice: no longer correctable here. */
    invoiced: boolean;
  } | null;
}

/**
 * MKS-266 — the approver's optional corrections, applied to the ENTRY the
 * approval writes (the request row keeps the employee's values). The same
 * shape is the body of a post-approval correction.
 */
export interface BackdatedHoursEntryValuesInput {
  hours?: number;
  task?: string;
  description?: string;
}

export interface CreateBackdatedHoursRequestInput {
  projectId: number;
  date: string;
  hours: number;
  task?: string;
  description?: string;
  reason: string;
}

export interface DeclineBackdatedHoursRequestInput {
  reason?: string;
}

export async function createBackdatedHoursRequest(
  input: CreateBackdatedHoursRequestInput,
): Promise<BackdatedHoursRequest> {
  const res = await apiPost<{ data: BackdatedHoursRequest }>('/backdated-hours-requests', input);
  return res.data;
}

export async function getMyBackdatedHoursRequests(
  state?: string,
): Promise<BackdatedHoursRequest[]> {
  const query = state ? `?state=${encodeURIComponent(state)}` : '';
  const res = await apiGet<{ data: BackdatedHoursRequest[] }>(
    `/backdated-hours-requests/my${query}`,
  );
  return res.data;
}

export async function getPendingBackdatedHoursRequests(
  scope?: 'all',
): Promise<PendingBackdatedHoursRequest[]> {
  const url =
    scope === 'all'
      ? '/admin/backdated-hours-requests/pending?scope=all'
      : '/admin/backdated-hours-requests/pending';
  const res = await apiGet<{ data: PendingBackdatedHoursRequest[] }>(url);
  return res.data;
}

export async function approveBackdatedHoursRequest(
  id: number,
  // MKS-266 — the approver's adjustments. Omitted (or `{}`) approves the
  // request exactly as filed.
  input: BackdatedHoursEntryValuesInput = {},
): Promise<BackdatedHoursRequest> {
  const res = await apiPost<{ data: BackdatedHoursRequest }>(
    `/admin/backdated-hours-requests/${id}/approve`,
    input,
  );
  return res.data;
}

/** MKS-266 — the approved requests of one project, with their entries. */
export async function getApprovedBackdatedHoursRequests(
  projectId: number,
): Promise<ApprovedBackdatedHoursRequest[]> {
  const res = await apiGet<{ data: ApprovedBackdatedHoursRequest[] }>(
    `/admin/backdated-hours-requests/approved?projectId=${projectId}`,
  );
  return res.data;
}

/** MKS-266 — correct the entry an approval created, month lock and all. */
export async function correctBackdatedHoursEntry(
  id: number,
  input: BackdatedHoursEntryValuesInput,
): Promise<ApprovedBackdatedHoursRequest> {
  const res = await apiPatch<{ data: ApprovedBackdatedHoursRequest }>(
    `/admin/backdated-hours-requests/${id}/entry`,
    input,
  );
  return res.data;
}

export async function declineBackdatedHoursRequest(
  id: number,
  input: DeclineBackdatedHoursRequestInput,
): Promise<BackdatedHoursRequest> {
  const res = await apiPost<{ data: BackdatedHoursRequest }>(
    `/admin/backdated-hours-requests/${id}/decline`,
    input,
  );
  return res.data;
}

export async function deleteBackdatedHoursRequest(id: number): Promise<void> {
  await apiDelete<{ data: { success: boolean } }>(`/backdated-hours-requests/${id}`);
}
